import * as PropTypes from "prop-types";

import React, { Fragment, PureComponent } from "react";
import decodeHTMLEntities from "core-web/utils/decodeHTMLEntities";

import Bold from "theme/libs/wordpress/components/Wysiwyg/tags/Bold";
import H1 from "theme/libs/wordpress/components/Wysiwyg/tags/H1";
import H2 from "theme/libs/wordpress/components/Wysiwyg/tags/H2";
import H3 from "theme/libs/wordpress/components/Wysiwyg/tags/H3";
import H4 from "theme/libs/wordpress/components/Wysiwyg/tags/H4";
import H5 from "theme/libs/wordpress/components/Wysiwyg/tags/H5";
import H6 from "theme/libs/wordpress/components/Wysiwyg/tags/H6";
import Italic from "theme/libs/wordpress/components/Wysiwyg/tags/Italic";
import ListItem from "theme/libs/wordpress/components/Wysiwyg/tags/ListItem";
import OrderedList from "theme/libs/wordpress/components/Wysiwyg/tags/OrderedList";
import Span from "theme/libs/wordpress/components/Wysiwyg/tags/Span";
import Underline from "theme/libs/wordpress/components/Wysiwyg/tags/Underline";
import UnorderedList from "theme/libs/wordpress/components/Wysiwyg/tags/UnorderedList";
import Url from "theme/libs/wordpress/components/Wysiwyg/tags/Url";

class WysiwygContainer extends PureComponent {
    validTags = [];

    static propTypes = {
        data: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.array])).isRequired,
        tagComponents: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.node, PropTypes.func])),
        textComponent: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.node, PropTypes.object])
            .isRequired,
    };

    static defaultProps = {
        tagComponents: {},
    };

    constructor(props) {
        super(props);
        this.validTags = Object.keys(props.tagComponents);
    }

    getComponent = (tag) => {
        if (this.validTags.indexOf(tag) !== -1) {
            const component = this.props.tagComponents[tag];
            return component ? component : null;
        }
        return null;
    };

    renderContent = (content, index, level = 0, renderedTags = [], subIndex = 0) => {
        let newContent = decodeHTMLEntities(String(content));

        if (level === 0 && !renderedTags[index]) {
            renderedTags[index] = [];
        }

        if (content !== undefined) {
            if (Array.isArray(content)) {
                // @TODO We might want to make the subIndex value more dynamic to handle deeper layers
                newContent = content.map((part, subIndex) =>
                    this.renderContent(part, index, level + 1, renderedTags, subIndex),
                );
            } else if (typeof content === "object") {
                const RenderComponent = this.getComponent(content.tag);
                if (RenderComponent) {
                    renderedTags[index].push(content.tag);
                    newContent = (
                        <RenderComponent attrs={content.attributes} key={`${index}.${level}.${subIndex}`}>
                            {this.renderContent(content.content, index, level + 1, renderedTags)}
                        </RenderComponent>
                    );
                } else {
                    // If it is not a valid tag, we just skip the tag and keep printing the content.
                    newContent = this.renderContent(content.content, index, level + 1, renderedTags);
                }
            }

            const noTextComponentTags = ["ol", "ul", "h1", "h2", "h3", "h4", "h5", "h6"];
            if (level === 0 && !renderedTags[index].filter((tag) => noTextComponentTags.indexOf(tag) !== -1).length) {
                // @TODO Fix the rest of the functionality:
                /* Output:
                    <wrapper>
                        <p>content</p>

                        <p><span style="font-weight: bold;">content</span>content</p>

                        <ul><li><p>content</p></li><li><p>content</p></li></ul>

                        <ol><li><p>content</p></li><li><p>content</p></li></ol>

                        <h1>content</h1>

                        <h2>content</h2>
                    </wrapper>
                */
                const TextComponent = this.props.textComponent;
                return <TextComponent>{newContent}</TextComponent>;
            }

            return newContent;
        }
    };

    render() {
        let { data } = this.props;

        //This is only to handle legacy strings.
        if (typeof data === "string") {
            data = data.split("\n");
        }

        //This is what actually comes from the cms (an array).
        return data.map((row, index) => {
            return <Fragment key={index}>{this.renderContent(row, index)}</Fragment>;
        });
    }
}

const Wysiwyg = ({ data, validTags, tagComponents, textComponent }) => {
    const components = {
        h1: H1,
        h2: H2,
        h3: H3,
        h4: H4,
        h5: H5,
        h6: H6,
        url: Url,
        i: Italic,
        b: Bold,
        u: Underline,
        ul: UnorderedList,
        ol: OrderedList,
        li: ListItem,
        span: Span,
        ...tagComponents,
    };

    const validComponents = {};
    validTags.forEach((tag) => {
        const tagComponent = components[tag];
        if (tagComponent) {
            validComponents[tag] = components[tag];
        }
    });

    return <WysiwygContainer data={data} textComponent={textComponent} tagComponents={validComponents} />;
};

Wysiwyg.propTypes = {
    data: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.array])).isRequired,
    tagComponents: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.node, PropTypes.func])),
    textComponent: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.node, PropTypes.object]),
    validTags: PropTypes.arrayOf(PropTypes.string),
};

Wysiwyg.defaultProps = {
    tagComponents: {},
    textComponent: (props) => <p {...props} />,
    validTags: ["url", "i", "b", "u", "ul", "ol", "li", "span", "h1", "h2", "h3", "h4", "h5", "h6"],
};

export default Wysiwyg;
